/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import type { AuthorProfile } from 'bundles/shared-articles/components/ArticleAuthorProfile';
import ArticleAuthorProfilePreview from 'bundles/shared-articles/components/ArticleAuthorProfilePreview';
import ArticleByLine from 'bundles/shared-articles/components/ArticleByLine';
import ArticleDisclaimerText from 'bundles/shared-articles/components/ArticleDisclaimerText';
import { DEFAULT_ARTICLE_AUTHOR } from 'bundles/shared-articles/constants';

import _t from 'i18n!nls/shared-articles';

export type Props = {
  authorNames?: string;
  date: string;
  title: string;
  authorsProfiles?: AuthorProfile[];
  shouldDisplayAuthorsProfile?: boolean;
};

const styles = {
  footerByLine: css`
    margin-bottom: var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      max-width: 100%;
    }
  `,
  authorsWrapper: css`
    margin: var(--cds-spacing-50) 0;
    display: block;
  `,
};

/**
 * Renders the footer of an article which includes author information and a disclaimer.
 * @param {Object} props - The properties for the article footer component.
 * @param {string} props.date - The publication date of the article.
 * @param {string} props.title - The title of the article.
 * @param {string[]} props.authorNames - The names of the authors.
 * @param {boolean} props.shouldDisplayAuthorsProfile - Flag indicating whether to display author profiles.
 * @param {AuthorProfile[]} props.authorsProfiles - Array of author profiles.
 * @returns {React.Element} The rendered article footer component.
 */
const ArticleFooter = ({ date, title, authorNames, shouldDisplayAuthorsProfile, authorsProfiles }: Props) => {
  let names;

  if (!shouldDisplayAuthorsProfile) {
    names = authorNames ?? DEFAULT_ARTICLE_AUTHOR.name;
  }

  return (
    <div css={styles.footerByLine}>
      <ArticleByLine date={date} title={title} authorNames={names} />
      {!!shouldDisplayAuthorsProfile && (
        <div data-testid="authors-profiles">
          <Typography2 component="span" variant="bodySecondary" css={styles.authorsWrapper}>
            {_t('Written by:')}
          </Typography2>
          {authorsProfiles?.length ? (
            authorsProfiles.map((author) => <ArticleAuthorProfilePreview key={author.url} {...author} />)
          ) : (
            <ArticleAuthorProfilePreview {...DEFAULT_ARTICLE_AUTHOR} />
          )}
        </div>
      )}
      <ArticleDisclaimerText
        message={_t(
          'This content has been made available for informational purposes only. Learners are advised to conduct additional research to ensure that courses and other credentials pursued meet their personal, professional, and financial goals.'
        )}
      />
    </div>
  );
};

export default ArticleFooter;
